// ConnectStripePage — shown post-signin when no active Stripe integration exists.
// Rendered outside AppShell (no sidebar) — full-page centered card.
//
// OAuth flow: POST /v1/oauth/stripe/start → { authorize_url } → navigate.
// The platform OAuth server returns the Stripe authorization URL; after
// the user approves, Stripe redirects to /v1/oauth/stripe/callback which
// sets up the integration and redirects back into the app. IntegrationGate
// then re-evaluates the integrations query and passes through to the dashboard.

import { useState } from "react";
import { CreditCard } from "lucide-react";
import { ApiError, apiFetch } from "@avagency/frontend/lib/api";
import { Button } from "@avagency/frontend/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@avagency/frontend/components/ui/card";
import { Footer } from "../components/Footer";

async function startStripeOAuth(): Promise<void> {
  // POST /v1/oauth/{provider}/start (platform/oauth/server/server.go:76)
  // returns { authorize_url: string }. return_url is REQUIRED (server
  // returns 400 missing_return_url when absent). We navigate the page
  // to authorize_url so the browser follows the Stripe OAuth redirect
  // with cookies intact; Stripe callback returns to return_url.
  const result = await apiFetch<{ authorize_url: string }>(
    "/v1/oauth/stripe/start",
    { method: "POST", json: { return_url: "/" } }
  );
  window.location.href = result.authorize_url;
}

export function ConnectStripePage() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConnect() {
    setPending(true);
    setError(null);
    try {
      await startStripeOAuth();
      // Navigation happens inside startStripeOAuth; no state cleanup needed.
    } catch (err) {
      setPending(false);
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to start OAuth flow. Please try again.");
      }
    }
  }

  return (
    <div className="dundial-auth-bg flex min-h-svh flex-col items-center justify-center gap-8 p-4">
      <div className="relative z-10 w-full max-w-md">
        <Card className="dundial-auth-card">
          <CardHeader className="items-center pb-4 text-center">
            {/* Branded icon glyph */}
            <div className="dundial-connect-icon" aria-hidden>
              <CreditCard className="h-6 w-6" />
            </div>
            <CardTitle className="text-xl" style={{ color: "#f4f6fa" }}>
              Connect your Stripe account
            </CardTitle>
            <CardDescription style={{ color: "#8b94a6" }}>
              DunDial monitors your Stripe account for failed payments and
              automatically retries them using decline-code-aware strategies.
            </CardDescription>
          </CardHeader>

          {/* What happens next — reassurance list */}
          <CardContent className="space-y-4">
            <ul className="space-y-2.5" aria-label="What DunDial does">
              {[
                { dot: "dundial-icon-ok",   text: "Monitors every failed payment in real time" },
                { dot: "dundial-icon-warn",  text: "Applies per-decline-code retry strategies" },
                { dot: "dundial-icon-muted", text: "Stripe Connect OAuth — revocable from your Stripe dashboard" },
              ].map(({ dot, text }) => (
                <li key={text} className="flex items-center gap-3 text-sm" style={{ color: "#8b94a6" }}>
                  <span className={`${dot} h-1.5 w-1.5 flex-shrink-0 rounded-full`} aria-hidden
                    style={{ background: "currentColor", width: 6, height: 6, borderRadius: "50%" }} />
                  {text}
                </li>
              ))}
            </ul>

            {error && (
              <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            )}

            <Button
              className="w-full"
              disabled={pending}
              onClick={handleConnect}
              aria-busy={pending}
            >
              {pending ? "Redirecting to Stripe…" : "Connect Stripe"}
            </Button>

            <p className="text-center font-mono text-[10px] uppercase tracking-widest" style={{ color: "#6e7a8e" }}>
              Stripe Connect OAuth · Revocable any time
            </p>
          </CardContent>
        </Card>
      </div>
      <div className="relative z-10">
        <Footer />
      </div>
    </div>
  );
}
